import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ImageIcon, Star } from 'lucide-react';

/* --------------------------------------------------------------- Section */

export const Section = ({ id, title, subtitle, action, className = '', children }) => (
  <section id={id} className={`py-8 sm:py-10 ${className}`}>
    <div className="lk-container">
      {(title || action) && (
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            {title && <h2 className="lk-heading">{title}</h2>}
            {subtitle && <p className="mt-1 text-sm text-ink-500">{subtitle}</p>}
          </div>
          {action && <div className="shrink-0 whitespace-nowrap">{action}</div>}
        </div>
      )}
      {children}
    </div>
  </section>
);

/* ------------------------------------------------------------------ Chip */

export const Chip = ({ active, children, className = '', ...props }) => (
  <button
    type="button"
    className={`lk-chip ${active ? 'lk-chip-active' : 'lk-chip-idle'} ${className}`}
    {...props}
  >
    {children}
  </button>
);

/* ------------------------------------------------------------------- Img */

/*
 * Branded stand-in for a missing or broken image. Provider uploads are
 * unreliable, and a grey box with a broken-image glyph reads as "this site is
 * broken" — on a poster wall it made half the cards look dead. Instead every
 * name gets a stable brand-family gradient with its initials, so a card without
 * art still looks deliberate and stays recognisable across pages (the venue
 * cover, its logo and its event cards all share one colour).
 */
const FALLBACK_TONES = [
  ['#0080ff', '#0a3d7a'], // LK blue
  ['#2b2f38', '#0f1115'], // ink
  ['#6b46c1', '#2e1a66'], // teaser violet
  ['#0e7490', '#0b3440'], // teal
  ['#c2410c', '#5c1d06'], // ember
];

const toneFor = (text = '') => {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (h * 31 + text.charCodeAt(i)) >>> 0;
  return FALLBACK_TONES[h % FALLBACK_TONES.length];
};

const initialsOf = (text = '') =>
  text
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

export const ImageFallback = ({ label = '', className = '', monogram = true }) => {
  const initials = initialsOf(label);
  if (!initials) {
    return (
      <div className={`flex items-center justify-center bg-surface-sunken text-ink-400 ${className}`}>
        <ImageIcon className="h-7 w-7" aria-hidden="true" />
      </div>
    );
  }
  const [from, to] = toneFor(label);
  return (
    <div
      className={`overflow-hidden ${className}`}
      style={{
        containerType: 'size',
        backgroundImage: `radial-gradient(120% 90% at 20% 10%, rgba(255,255,255,.22), transparent 55%), linear-gradient(160deg, ${from}, ${to})`,
      }}
      aria-hidden="true"
    >
      {/* Sized in container units (cqmin) so the same fallback reads as a
          56px logo and a full-width cover — capped at 4.5rem so a large tile
          gets a quiet monogram rather than a shouting one. */}
      {monogram && (
      <span
        className="flex h-full w-full select-none items-center justify-center font-extrabold tracking-tight text-white/60"
        style={{ fontSize: 'clamp(0.8rem, 36cqmin, 4.5rem)', lineHeight: 1 }}
      >
        {initials}
      </span>
      )}
    </div>
  );
};

/**
 * Image that fades in, shows a shimmer while loading and degrades to a branded
 * placeholder (see ImageFallback) instead of a broken-image icon.
 * `label` names the fallback when `alt` is intentionally empty; `fit="contain"`
 * letterboxes (used for posters over a blurred backdrop).
 */
export const Img = ({
  src,
  alt = '',
  label,
  className = '',
  imgClassName = '',
  ratio,
  fit = 'cover',
  // false where text is overlaid on the image (venue covers) so the monogram
  // never sits underneath a title.
  monogram = true,
}) => {  const [state, setState] = useState(src ? 'loading' : 'error');
  const nodeRef = useRef(null);

  /*
   * A cached image can finish decoding before React attaches `onLoad`, so that
   * event never fires and the fade-in never runs — the poster stays at
   * opacity-0 forever. It shows up as a blank card on any warm load (reload,
   * back navigation, a re-render that remounts the node) while the network tab
   * insists the image downloaded fine. So never wait on `onLoad` alone: settle
   * from `complete`, which is already true for anything served from cache.
   */
  const settle = useCallback((node) => {
    if (!node || !node.complete) return false;
    setState(node.naturalWidth > 0 ? 'ready' : 'error');
    return true;
  }, []);

  const attach = useCallback(
    (node) => {
      nodeRef.current = node;
      settle(node);
    },
    [settle]
  );

  useEffect(() => {
    // Re-check after paint too: the load can also land in the gap between the
    // ref callback and the listener being live.
    if (!settle(nodeRef.current)) setState(src ? 'loading' : 'error');
  }, [src, settle]);

  return (
    <div
      className={`relative overflow-hidden ${fit === 'contain' ? '' : 'bg-surface-sunken'} ${className}`}
      style={ratio ? { aspectRatio: ratio } : undefined}
    >
      {state === 'loading' && <div className="absolute inset-0 lk-shimmer" />}
      {state === 'error' ? (
        <ImageFallback label={label ?? alt} monogram={monogram} className="absolute inset-0" />
      ) : (
        <img
          key={src}
          ref={attach}
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setState('ready')}
          onError={() => setState('error')}
          className={`h-full w-full ${fit === 'contain' ? 'object-contain' : 'object-cover'} transition-opacity duration-500 ${
            state === 'ready' ? 'opacity-100' : 'opacity-0'
          } ${imgClassName}`}
        />
      )}
    </div>
  );
};

/* ------------------------------------------------------------- ScrollRow */

/**
 * Horizontal rail with arrow controls on desktop and native swipe on touch.
 * Arrows hide themselves when there's nothing to scroll to.
 */
export const ScrollRow = ({ children, className = '', itemClassName = '' }) => {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({
      start: el.scrollLeft <= 8,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8,
    });
  }, []);

  useEffect(() => {
    sync();
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      el.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [sync, children]);

  const nudge = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 240), behavior: 'smooth' });
  };

  const arrow = 'absolute top-1/2 z-10 hidden -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-white shadow-cardHover text-ink-900 transition hover:scale-105 md:flex';

  return (
    <div className={`group relative ${className}`}>
      {!edges.start && (
        <button type="button" aria-label="Scroll left" onClick={() => nudge(-1)} className={`${arrow} -left-4`}>
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      <div
        ref={ref}
        className="scrollbar-hide -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-3 pt-1 sm:mx-0 sm:scroll-px-0 sm:px-0"
      >
        {React.Children.map(children, (child, i) => (
          <div key={i} className={`snap-start shrink-0 ${itemClassName}`}>
            {child}
          </div>
        ))}
      </div>
      {!edges.end && (
        <button type="button" aria-label="Scroll right" onClick={() => nudge(1)} className={`${arrow} -right-4`}>
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------- Rating */

export const Rating = ({ value = 0, count = 0, className = '' }) => {
  if (!value) return null;
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
      <span className="text-sm font-semibold text-ink-900">{Number(value).toFixed(1)}</span>
      {count > 0 && <span className="text-xs text-ink-500">({count})</span>}
    </div>
  );
};

/* ------------------------------------------------------------ EmptyState */

export const EmptyState = ({ icon: Icon, title, message, action, className = '' }) => (
  <div className={`lk-card flex flex-col items-center px-6 py-16 text-center ${className}`}>
    {Icon && (
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-surface-sunken">
        <Icon className="h-7 w-7 text-ink-400" />
      </div>
    )}
    <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
    {message && <p className="mt-1 max-w-sm text-sm text-ink-500">{message}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

/* -------------------------------------------------------------- Skeleton */

export const Shimmer = ({ className = '' }) => (
  <div className={`lk-shimmer rounded-lg ${className}`} />
);

/* --------------------------------------------------------- ExpandableText */

/**
 * Text clamped to `lines`, with a "More details" toggle that appears only when
 * the text is actually cut off. Ticket descriptions carry real conditions
 * ("1 female compulsory", entry rules) that the clamp used to hide on phones
 * with no way to read them.
 */
export const ExpandableText = ({ text, lines = 2, className = '' }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);

  // Measure while clamped; re-measure when the width changes (rotation,
  // resize) since that decides whether the text still overflows.
  useEffect(() => {
    const node = ref.current;
    if (!node || open) return undefined;
    const measure = () => setClipped(node.scrollHeight > node.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text, open]);

  if (!text) return null;
  const clamp = { 1: 'line-clamp-1', 2: 'line-clamp-2', 3: 'line-clamp-3' }[lines] || 'line-clamp-2';

  return (
    <div className={className}>
      <p ref={ref} className={`whitespace-pre-line text-sm text-ink-500 ${open ? '' : clamp}`}>
        {text}
      </p>
      {(clipped || open) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-expanded={open}
          className="lk-focus -ml-1 mt-0.5 inline-flex min-h-[32px] items-center gap-0.5 rounded px-1 text-xs font-semibold text-brand-secondary"
        >
          {open ? 'Less' : 'More details'}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
